import React, { useState, useRef, useEffect } from 'react';
import SkillsInput from '../components/SkillsInput';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS } from '../../../../config';
import { api } from '../../../../utils/api';
import '../../../../styles/profile/preferences-tab.css';
import '../../../../styles/contact-modal.css';

interface Preferences {
  uid: string | null;
  primary_role_preference: string | null;
  primary_role_experience: string | null;
  other_roles_preferences: string[] | null;
  industries_preferences: string[] | null;
  exact_salary: number | null;
  salary_currency: string | null;
  experience_level: string | null;
  job_types: string[] | null;
  open_to_work_environment: string[] | null;
  preferred_work_environment: string | null;
  location_preferences: string[] | null;
  willing_to_relocate: boolean | null;
}

interface UserData {
  preferences: Preferences | null;
}

interface PreferencesTabProps {
  userData: UserData | null;
  preferences: Preferences | null;
}

export default function PreferencesTab({ userData, preferences }: PreferencesTabProps) {
  const [preferencesId, setPreferencesId] = useState<string | null>(null);
  
  const [primaryRole, setPrimaryRole] = useState('');
  const [primaryRoleExperience, setPrimaryRoleExperience] = useState('Years of Experience');
  const [experienceDropdownOpen, setExperienceDropdownOpen] = useState(false);
  const experienceDropdownRef = useRef<HTMLDivElement>(null);
  const [otherRoles, setOtherRoles] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [salary, setSalary] = useState('');
  const [currency, setCurrency] = useState('$');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);

  const CURRENCIES = ['$', '€', '£', '¥', '₽', 'Fr', 'kr', 'zł', 'R$', 'C$', 'A$'];
  
  const [experienceLevel, setExperienceLevel] = useState<'entry' | 'mid' | 'senior'>('entry');
  
  const [jobTypes, setJobTypes] = useState({
    fullTime: false,
    partTime: false,
    contractor: false,
    internship: false,
  });
  
  const [openToEnvironments, setOpenToEnvironments] = useState({
    remote: false,
    hybrid: false,
    onSite: false,
  });
  const [preferredEnvironment, setPreferredEnvironment] = useState<'remote' | 'hybrid' | 'onsite'>('onsite');
  const [willingToRelocate, setWillingToRelocate] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);

  const [isDirty, setIsDirty] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [initialValues, setInitialValues] = useState({
    primaryRole: '',
    primaryRoleExperience: 'Years of Experience',
    otherRoles: [] as string[],
    industries: [] as string[],
    salary: '',
    currency: '$',
    experienceLevel: 'entry' as 'entry' | 'mid' | 'senior',
    jobTypes: { fullTime: false, partTime: false, contractor: false, internship: false },
    openToEnvironments: { remote: false, hybrid: false, onSite: false },
    preferredEnvironment: 'onsite' as 'remote' | 'hybrid' | 'onsite',
    willingToRelocate: false,
    locations: [] as string[],
  });

  const experienceOptions = [
    '< 1 Year',
    ...Array.from({ length: 10 }, (_, i) => `${i + 1} Year${i + 1 > 1 ? 's' : ''}`),
    '> 10 Years'
  ];

  useEffect(() => {
    if (preferences) {
      const jobTypesArray = preferences.job_types || [];
      const openToArray = preferences.open_to_work_environment || [];

      const newValues = {
        primaryRole: preferences.primary_role_preference || '',
        primaryRoleExperience: preferences.primary_role_experience || 'Years of Experience',
        otherRoles: preferences.other_roles_preferences || [],
        industries: preferences.industries_preferences || [],
        salary: preferences.exact_salary?.toString() || '',
        currency: preferences.salary_currency || '$',
        experienceLevel: (preferences.experience_level || 'entry') as 'entry' | 'mid' | 'senior',
        jobTypes: {
          fullTime: jobTypesArray.includes('Full Time'),
          partTime: jobTypesArray.includes('Part Time'),
          contractor: jobTypesArray.includes('Contractor'),
          internship: jobTypesArray.includes('Internship'),
        },
        openToEnvironments: {
          remote: openToArray.includes('Remote'),
          hybrid: openToArray.includes('Hybrid'),
          onSite: openToArray.includes('On Site'),
        },
        preferredEnvironment: (preferences.preferred_work_environment || 'onsite') as 'remote' | 'hybrid' | 'onsite',
        willingToRelocate: preferences.willing_to_relocate ?? false,
        locations: preferences.location_preferences || [],
      };

      setPreferencesId(preferences.uid);
      setPrimaryRole(newValues.primaryRole);
      setPrimaryRoleExperience(newValues.primaryRoleExperience);
      setOtherRoles(newValues.otherRoles);
      setIndustries(newValues.industries);
      setSalary(newValues.salary);
      setCurrency(newValues.currency);
      setExperienceLevel(newValues.experienceLevel);
      setJobTypes(newValues.jobTypes);
      setOpenToEnvironments(newValues.openToEnvironments);
      setPreferredEnvironment(newValues.preferredEnvironment);
      setWillingToRelocate(newValues.willingToRelocate);
      setLocations(newValues.locations);

      setInitialValues(newValues);
      setIsDirty(false);
    }
  }, [preferences]);

  useEffect(() => {
    const hasChanged =
      primaryRole !== initialValues.primaryRole ||
      primaryRoleExperience !== initialValues.primaryRoleExperience ||
      JSON.stringify(otherRoles) !== JSON.stringify(initialValues.otherRoles) ||
      JSON.stringify(industries) !== JSON.stringify(initialValues.industries) ||
      salary !== initialValues.salary ||
      currency !== initialValues.currency ||
      experienceLevel !== initialValues.experienceLevel ||
      JSON.stringify(jobTypes) !== JSON.stringify(initialValues.jobTypes) ||
      JSON.stringify(openToEnvironments) !== JSON.stringify(initialValues.openToEnvironments) ||
      preferredEnvironment !== initialValues.preferredEnvironment ||
      willingToRelocate !== initialValues.willingToRelocate ||
      JSON.stringify(locations) !== JSON.stringify(initialValues.locations);

    setIsDirty(hasChanged);
  }, [
    primaryRole, primaryRoleExperience, otherRoles, industries, salary, currency,
    experienceLevel, jobTypes, openToEnvironments, preferredEnvironment,
    willingToRelocate, locations, initialValues
  ]);

  const handleImport = () => {
  };

  const handleUpdate = async () => {
    setIsUpdating(true);

    try {
      const payload: any = {};

      if (preferencesId) {
        payload.uid = preferencesId;
      }

      if (primaryRole.trim()) payload.primary_role_preference = primaryRole.trim();
      if (primaryRoleExperience !== 'Years of Experience') payload.primary_role_experience = primaryRoleExperience;
      if (otherRoles.length > 0) payload.other_roles_preferences = otherRoles;
      if (industries.length > 0) payload.industries_preferences = industries;
      if (salary) payload.exact_salary = parseInt(salary);
      if (currency) payload.salary_currency = currency;
      if (experienceLevel) payload.experience_level = experienceLevel;
      
      const jobTypesArray = [];
      if (jobTypes.fullTime) jobTypesArray.push('Full Time');
      if (jobTypes.partTime) jobTypesArray.push('Part Time');
      if (jobTypes.contractor) jobTypesArray.push('Contractor');
      if (jobTypes.internship) jobTypesArray.push('Internship');
      if (jobTypesArray.length > 0) payload.job_types = jobTypesArray;

      const openToArray = [];
      if (openToEnvironments.remote) openToArray.push('Remote');
      if (openToEnvironments.hybrid) openToArray.push('Hybrid');
      if (openToEnvironments.onSite) openToArray.push('On Site');
      if (openToArray.length > 0) payload.open_to_work_environment = openToArray;

      if (preferredEnvironment) payload.preferred_work_environment = preferredEnvironment;
      if (locations.length > 0) payload.location_preferences = locations;
      payload.willing_to_relocate = willingToRelocate;


      const response = await api.post(API_ENDPOINTS.users.updatePreferences, payload);
      

      if (response && response.uid && !preferencesId) {
        setPreferencesId(response.uid);
      }

      setInitialValues({
        primaryRole,
        primaryRoleExperience,
        otherRoles,
        industries,
        salary,
        currency,
        experienceLevel,
        jobTypes,
        openToEnvironments,
        preferredEnvironment,
        willingToRelocate,
        locations,
      });
      setIsDirty(false);

      setToast({ message: 'Preferences updated successfully! ✓', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update preferences. Please try again.', type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const formatSalary = (value: string) => {
    if (!value) return '';
    const numericValue = value.replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    return parseInt(numericValue).toLocaleString('en-US');
  };

  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setSalary(value);
  };

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (experienceDropdownRef.current && !experienceDropdownRef.current.contains(e.target as Node)) {
        setExperienceDropdownOpen(false);
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setShowCurrencyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="preferences-tab contact-modal">
      <div className="preferences-header">
        <h1 className="preferences-title">Preferences</h1>
        <div className="preferences-actions">
          <button className="preferences-btn preferences-btn--import" onClick={handleImport} type="button">
            <span className="preferences-btn-icon">⬇</span>
            Import
          </button>
          <button 
            className="preferences-btn preferences-btn--update" 
            onClick={handleUpdate} 
            type="button"
            disabled={!isDirty || isUpdating}
            style={{ 
              opacity: (!isDirty || isUpdating) ? 0.5 : 1,
              cursor: (!isDirty || isUpdating) ? 'not-allowed' : 'pointer'
            }}
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>

      <div className="preferences-section-header">Job Preferences</div>

      <div className="preferences-grid">
        <div className="preferences-field">
          <label className="preferences-label">Primary Role Preference</label>
          <div className="preferences-description">What is the primary role you are interested in?</div>
          <input
            type="text"
            className="cm-input"
            value={primaryRole}
            onChange={(e) => setPrimaryRole(e.target.value)}
            placeholder="Role"
          />
        </div>

        <div className="preferences-field">
          <label className="preferences-label">Primary Role Experience</label>
          <div className="preferences-description">How many years of experience in your primary role of preference?</div>
          <div className="preferences-dropdown" ref={experienceDropdownRef}>
            <button
              type="button"
              className="preferences-dropdown-trigger"
              onClick={() => setExperienceDropdownOpen(!experienceDropdownOpen)}
            >
              <span className="preferences-dropdown-value">{primaryRoleExperience}</span>
              <span className="preferences-dropdown-arrow">▾</span>
            </button>
            {experienceDropdownOpen && (
              <div className="preferences-dropdown-menu">
                {experienceOptions.map((option) => (
                  <div
                    key={option}
                    className={`preferences-dropdown-item ${primaryRoleExperience === option ? 'preferences-dropdown-item--selected' : ''}`}
                    onClick={() => {
                      setPrimaryRoleExperience(option);
                      setExperienceDropdownOpen(false);
                    }}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="preferences-field-full">
        <label className="preferences-label">Other Roles</label>
        <div className="preferences-description">Any other roles you are interested in?</div>
        <SkillsInput skills={otherRoles} onChange={setOtherRoles} placeholder="Add other roles..." />
      </div>

      <div className="preferences-field-full">
        <label className="preferences-label">Industries</label>
        <div className="preferences-description">What industries would you like to work in?</div>
        <SkillsInput skills={industries} onChange={setIndustries} placeholder="Add industries..." />
      </div>

      <div className="preferences-field-full">
        <label className="preferences-label">Salary</label>
        <div className="preferences-description">What is your annual salary expectation?</div>
        <div className="preferences-salary-wrapper" ref={currencyDropdownRef}>
          <button
            type="button"
            className="preferences-currency-btn"
            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
          >
            {currency}
          </button>
          {showCurrencyDropdown && (
            <div className="preferences-currency-dropdown">
              {CURRENCIES.map((curr) => (
                <button
                  key={curr}
                  type="button"
                  className={`preferences-currency-option ${currency === curr ? 'active' : ''}`}
                  onClick={() => {
                    handleCurrencyChange(curr);
                    setShowCurrencyDropdown(false);
                  }}
                >
                  {curr}
                </button>
              ))}
            </div>
          )}
          <input
            type="text"
            className="cm-input preferences-salary-input"
            placeholder="+ add salary"
            value={formatSalary(salary)}
            onChange={handleSalaryChange}
          />
        </div>
      </div>

      <div className="preferences-grid">
        <div className="preferences-field">
          <label className="preferences-label">Experience Level</label>
          <div className="preferences-description">Select which level of experience you are looking for in your next job:</div>
          <div className="preferences-radio-group">
            <label className="preferences-radio">
              <input
                type="radio"
                name="experience-level"
                checked={experienceLevel === 'entry'}
                onChange={() => setExperienceLevel('entry')}
              />
              <span>Entry</span>
            </label>
            <label className="preferences-radio">
              <input
                type="radio"
                name="experience-level"
                checked={experienceLevel === 'mid'}
                onChange={() => setExperienceLevel('mid')}
              />
              <span>Mid Level</span>
            </label>
            <label className="preferences-radio">
              <input
                type="radio"
                name="experience-level"
                checked={experienceLevel === 'senior'}
                onChange={() => setExperienceLevel('senior')}
              />
              <span>Senior</span>
            </label>
          </div>
        </div>

        <div className="preferences-field">
          <label className="preferences-label">Job Types</label>
          <div className="preferences-description">Which types of jobs are you open to working in:</div>
          <div className="preferences-checkbox-group">
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={jobTypes.fullTime}
                onChange={(e) => setJobTypes({ ...jobTypes, fullTime: e.target.checked })}
              />
              <span>Full Time</span>
            </label>
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={jobTypes.partTime}
                onChange={(e) => setJobTypes({ ...jobTypes, partTime: e.target.checked })}
              />
              <span>Part Time</span>
            </label>
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={jobTypes.contractor}
                onChange={(e) => setJobTypes({ ...jobTypes, contractor: e.target.checked })}
              />
              <span>Contractor</span>
            </label>
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={jobTypes.internship}
                onChange={(e) => setJobTypes({ ...jobTypes, internship: e.target.checked })}
              />
              <span>Internship</span>
            </label>
          </div>
        </div>
      </div>

      <div className="preferences-section-header">Location & Environment</div>

      <div className="preferences-grid">
        <div className="preferences-field">
          <label className="preferences-label">Open to work environments</label>
          <div className="preferences-description">Which types of work environments are you open to working in:</div>
          <div className="preferences-checkbox-group">
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={openToEnvironments.remote}
                onChange={(e) => setOpenToEnvironments({ ...openToEnvironments, remote: e.target.checked })}
              />
              <span>Remote</span>
            </label>
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={openToEnvironments.hybrid}
                onChange={(e) => setOpenToEnvironments({ ...openToEnvironments, hybrid: e.target.checked })}
              />
              <span>Hybrid</span>
            </label>
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={openToEnvironments.onSite}
                onChange={(e) => setOpenToEnvironments({ ...openToEnvironments, onSite: e.target.checked })}
              />
              <span>On Site</span>
            </label>
          </div>
        </div>

        <div className="preferences-field">
          <label className="preferences-label">Preferred work environment</label>
          <div className="preferences-description">Which work environment do you prefer to work in (optional):</div>
          <div className="preferences-radio-group">
            <label className="preferences-radio">
              <input
                type="radio"
                name="preferred-environment"
                checked={preferredEnvironment === 'remote'}
                onChange={() => setPreferredEnvironment('remote')}
              />
              <span>Remote</span>
            </label>
            <label className="preferences-radio">
              <input
                type="radio"
                name="preferred-environment"
                checked={preferredEnvironment === 'hybrid'}
                onChange={() => setPreferredEnvironment('hybrid')}
              />
              <span>Hybrid</span>
            </label>
            <label className="preferences-radio">
              <input
                type="radio"
                name="preferred-environment"
                checked={preferredEnvironment === 'onsite'}
                onChange={() => setPreferredEnvironment('onsite')}
              />
              <span>On Site</span>
            </label>
          </div>
        </div>
      </div>

      <div className="preferences-grid">
        <div className="preferences-field">
          <label className="preferences-label">Locations</label>
          <div className="preferences-description">For on-site or hybrid positions, enter all cities in which you are open to working at.</div>
          <SkillsInput skills={locations} onChange={setLocations} placeholder="Add locations..." />
        </div>

        <div className="preferences-field">
          <label className="preferences-label">Other</label>
          <div className="preferences-checkbox-group" style={{ marginTop: '24px' }}>
            <label className="preferences-checkbox">
              <input
                type="checkbox"
                checked={willingToRelocate}
                onChange={(e) => setWillingToRelocate(e.target.checked)}
              />
              <span>Willing to relocate?</span>
            </label>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
